import React, { FC } from 'react';

import { ValidationIssue } from '@intelligent-play-v2/domain';
import { Button, ButtonType } from '~/components/button';

interface OnboardingPasteStepProps {
  tsv: string;
  onTsvChange: (tsv: string) => void;
  onParse: () => void;
  isParsing: boolean;
  /** Blocking problems from the last parse attempt; empty when there were none. */
  errors: ValidationIssue[];
  /** True when the last parse matched columns by position because no header row was found. */
  matchedByPosition: boolean;
}

export const OnboardingPasteStep: FC<OnboardingPasteStepProps> = ({
  tsv,
  onTsvChange,
  onParse,
  isParsing,
  errors,
  matchedByPosition,
}) => {
  const hasErrors = errors.length > 0;

  return (
    <div className="flex flex-col space-y-4">
      <div className="space-y-2 text-base text-primary-600">
        <p>
          Open the <code>New_Forms</code> tab in the master spreadsheet, copy
          the new response row or rows — including the header row if you can —
          and paste them below. The header row lets the columns be matched by
          name, so a reordered spreadsheet still comes through correctly.
        </p>
        <p>
          One paste covers one facility. Several rows for the same facility is
          how a multi-pitch facility is onboarded.
        </p>
        <p>
          <span className="font-semibold">Only the New_Forms tab.</span> Every
          other tab is worked out with formulas and contains errors.
        </p>
      </div>

      <textarea
        className="w-full rounded border border-primary-250 bg-white p-3 font-mono text-sm outline-none"
        rows={12}
        value={tsv}
        placeholder="Paste the tab-separated rows here — header row first, then one row per pitch"
        onChange={event => onTsvChange(event.target.value)}
      />

      {hasErrors && (
        <div className="rounded border border-red bg-red-lightest p-4 text-red-dark">
          <p className="text-base font-semibold">
            This paste can&apos;t be used yet
          </p>
          <ul className="mt-2 space-y-1">
            {errors.map((issue, index) => (
              <li key={`${issue.path}-${index}`} className="text-base">
                {!!issue.path && (
                  <code className="mr-2 text-sm">{issue.path}</code>
                )}
                {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Errors already say the paste is unusable, so the positional note would only be noise alongside them. */}
      {matchedByPosition && !hasErrors && (
        <div className="rounded border border-yellow bg-yellow-lightest p-4">
          <p className="text-base">
            No header row was detected, so the columns were matched by position.
            Check the values on the next step carefully.
          </p>
        </div>
      )}

      <div className="w-48">
        <Button
          text="Parse paste"
          onClick={onParse}
          disabled={tsv.trim() === '' || isParsing}
          isLoading={isParsing}
          type={ButtonType.Primary}
        />
      </div>
    </div>
  );
};
